import { httpClient } from '../lib/http.interceptor';

export interface Event {
  _id: string;
  id: string;
  nome: string;
  descricao?: string;
  dataInicio: string;
  dataFim: string;
  local?: string;
  status: 'draft' | 'scheduled' | 'active' | 'completed' | 'cancelled';
  agencyId?: string;
  solutionIds?: string[];
  maxParticipantes?: number;
  participantesInscritos?: number;
  createdAt?: string;
  updatedAt?: string;
}

class EventService {
  async getEventsByAgency(_agencyId: string): Promise<Event[]> {
    try {
      // Backend should filter by the logged user's relationship with events
      // For agency users, it should return events where organizerId matches
      const response = await httpClient.get('/events');
      
      console.log('Response from /events:', response.data);
      
      let events: Event[] = [];
      if (response.data && response.data.data && Array.isArray(response.data.data)) {
        events = response.data.data;
      } else if (Array.isArray(response.data)) {
        events = response.data;
      }
      
      // Garantir que cada evento tenha um id
      events = events.map(event => ({
        ...event,
        id: event.id || event._id,
        _id: event._id || event.id
      }));
      
      console.log('Events after processing:', events);
      return events;
    } catch (error) {
      console.error('Error fetching events by agency:', error);
      return [];
    }
  }

  async getEventSolutions(eventId: string): Promise<string[]> {
    try {
      const response = await httpClient.get(`/events/${eventId}/solutions`);
      
      if (Array.isArray(response.data)) {
        return response.data;
      }
      
      if (response.data && Array.isArray(response.data.solutionIds)) {
        return response.data.solutionIds;
      }
      
      return [];
    } catch (error) {
      console.error('Error fetching event solutions:', error);
      return [];
    }
  }

  async getEvents(params?: {
    page?: number;
    limit?: number;
    status?: string;
  }): Promise<{ data: Event[]; total: number }> {
    try {
      const response = await httpClient.get('/events', {
        params: {
          page: params?.page,
          limit: params?.limit,
          status: params?.status,
        },
      });

      if (response.data && response.data.data && Array.isArray(response.data.data)) {
        return {
          data: response.data.data,
          total: response.data.total || response.data.data.length,
        };
      }
      
      if (Array.isArray(response.data)) {
        return {
          data: response.data,
          total: response.data.length,
        };
      }

      return { data: [], total: 0 };
    } catch (error) {
      console.error('Error fetching events:', error);
      throw error;
    }
  }

  async getEventById(id: string): Promise<Event> {
    try {
      const response = await httpClient.get(`/events/${id}`);
      
      // Handle potential wrapper in response
      const eventData = response.data.data || response.data;
      
      // Normalize the event data structure - SEMPRE mapear inglês para português
      const normalizedEvent = {
        ...eventData,
        id: eventData.id || eventData._id,
        _id: eventData._id || eventData.id,
        // IMPORTANTE: Sempre usar os campos em inglês como fonte primária
        nome: eventData.name || eventData.nome || '',
        dataFim: eventData.endDate || eventData.dataFim || '',
        dataInicio: eventData.startDate || eventData.dataInicio || '',
        descricao: eventData.description || eventData.descricao || '',
        // Handle solutions array properly - extract solutionId from objects
        solutionIds: Array.isArray(eventData.solutionIds) ? eventData.solutionIds : 
                     Array.isArray(eventData.solutions) ? eventData.solutions.map((s: any) => 
                       typeof s === 'string' ? s : s.solutionId || s.id || s) : [],
        status: eventData.status || 'active'
      };
      return normalizedEvent;
    } catch (error) {
      console.error('Error fetching event:', error);
      throw error;
    }
  }

  async createEvent(event: Partial<Event>): Promise<Event> {
    try {
      const response = await httpClient.post('/events', event);
      return response.data;
    } catch (error) {
      console.error('Error creating event:', error);
      throw error;
    }
  }

  async updateEvent(id: string, event: Partial<Event>): Promise<Event> {
    try {
      const response = await httpClient.put(`/events/${id}`, event);
      return response.data;
    } catch (error) {
      console.error('Error updating event:', error);
      throw error;
    }
  }

  async deleteEvent(id: string): Promise<void> {
    try {
      await httpClient.delete(`/events/${id}`);
    } catch (error) {
      console.error('Error deleting event:', error);
      throw error;
    }
  }
}

export const eventService = new EventService();